import React from 'react';
import image from '../../../../Assets/teacherFeedback.png';

/**
 * PDF-compatible checkbox style helper - using inline styles for reliable html2canvas rendering
 */
const getCheckboxStyle = (checked) => ({
    width: '16px',
    height: '16px',
    minWidth: '16px',
    minHeight: '16px',
    border: checked ? '2px solid #F97316' : '2px solid #9CA3AF',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: checked ? '#F97316' : '#ffffff',
    marginRight: '8px'
});

/**
 * A styled colorful progress wheel component that matches the reference image.
 * @param {object} props
 * @param {object} props.data - The progress wheel data
 */
const ProgressWheel = ({ data = {} }) => {
    // Default values if no data is provided
    const wheelData = data || {
        awareness: { student: 'P', peer: 'P', teacher: 'P' },
        sensitivity: { student: 'P', peer: 'P', teacher: 'P' },
        creativity: { student: 'P', peer: 'P', teacher: 'P' }
    };

    return (
        <div className="flex items-start justify-between">
            {/* Main Wheel */}
            <div className="w-3/4">
                <svg viewBox="0 0 600 600" className="w-full max-w-md mx-auto">
                    {/* Background Circle Segments - Awareness (Pink) */}
                    <path d="M 300 300 L 300 0 A 300 300 0 0 1 550 150 Z" fill="#f472b6" />
                    {/* Student segment */}
                    <path d="M 300 300 L 300 100 A 200 200 0 0 1 483 200 L 300 300 Z" fill="#4ade80" />
                    {/* Peer segment */}
                    <path d="M 300 300 L 483 200 A 200 200 0 0 1 550 300 L 300 300 Z" fill="#a5b4fc" />

                    {/* Background Circle Segments - Sensitivity (Yellow) */}
                    <path d="M 300 300 L 550 150 A 300 300 0 0 1 550 450 Z" fill="#fef08a" />
                    {/* Student segment */}
                    <path d="M 300 300 L 550 300 A 200 200 0 0 1 483 400 L 300 300 Z" fill="#4ade80" />
                    {/* Peer segment */}
                    <path d="M 300 300 L 483 400 A 200 200 0 0 1 300 500 L 300 300 Z" fill="#a5b4fc" />

                    {/* Background Circle Segments - Creativity (Blue) */}
                    <path d="M 300 300 L 550 450 A 300 300 0 0 1 300 600 L 300 300 Z" fill="#7dd3fc" />
                    {/* Teacher segment */}
                    <path d="M 300 300 L 300 500 A 200 200 0 0 1 117 400 L 300 300 Z" fill="#f97316" />
                    {/* Student segment */}
                    <path d="M 300 300 L 117 400 A 200 200 0 0 1 50 300 L 300 300 Z" fill="#4ade80" />

                    {/* Background Circle Segments - Awareness (continued) */}
                    <path d="M 300 300 L 300 600 A 300 300 0 0 1 50 450 Z" fill="#f472b6" />
                    {/* Peer segment */}
                    <path d="M 300 300 L 50 300 A 200 200 0 0 1 117 200 L 300 300 Z" fill="#a5b4fc" />
                    {/* Teacher segment */}
                    <path d="M 300 300 L 117 200 A 200 200 0 0 1 300 100 L 300 300 Z" fill="#f97316" />

                    {/* Background Circle Segments - Creativity (continued) */}
                    <path d="M 300 300 L 50 450 A 300 300 0 0 1 50 150 Z" fill="#7dd3fc" />
                    {/* Peer segment */}
                    <path d="M 300 300 L 50 150 A 200 200 0 0 1 117 100 L 300 300 Z" fill="#a5b4fc" />
                    {/* Teacher segment */}
                    <path d="M 300 300 L 117 100 A 200 200 0 0 1 300 0 L 300 300 Z" fill="#f97316" />

                    {/* Inner Circles and Dividing Lines */}
                    <circle cx="300" cy="300" r="200" fill="none" stroke="#000" strokeWidth="2" />
                    <circle cx="300" cy="300" r="150" fill="none" stroke="#000" strokeWidth="2" />
                    <circle cx="300" cy="300" r="100" fill="none" stroke="#000" strokeWidth="2" />
                    <circle cx="300" cy="300" r="50" fill="white" stroke="#000" strokeWidth="2" />

                    {/* Dividing lines */}
                    <line x1="300" y1="0" x2="300" y2="600" stroke="#000" strokeWidth="2" />
                    <line x1="0" y1="300" x2="600" y2="300" stroke="#000" strokeWidth="2" />
                    <line x1="117" y1="100" x2="483" y2="500" stroke="#000" strokeWidth="2" />
                    <line x1="117" y1="500" x2="483" y2="100" stroke="#000" strokeWidth="2" />

                    {/* Center Text */}
                    <text x="300" y="295" textAnchor="middle" className="text-sm font-bold">PERFORMANCE</text>
                    <text x="300" y="315" textAnchor="middle" className="text-sm font-bold">LEVEL</text>

                    {/* Labels for Sections */}
                    <text x="300" y="40" textAnchor="middle" className="text-base font-bold"
                        transform="rotate(0, 300, 40)">AWARENESS
                    </text>
                    <text x="520" y="300" textAnchor="middle" className="text-base font-bold"
                        transform="rotate(90, 520, 300)">SENSITIVITY
                    </text>
                    <text x="300" y="560" textAnchor="middle" className="text-base font-bold"
                        transform="rotate(180, 300, 560)">CREATIVITY
                    </text>

                    {/* Perspective Labels */}
                    <text x="190" y="150" textAnchor="middle" className="text-xs font-bold">TEACHER</text>
                    <text x="300" y="190" textAnchor="middle" className="text-xs font-bold">STUDENT</text>
                    <text x="410" y="150" textAnchor="middle" className="text-xs font-bold">PEER</text>

                    <text x="520" y="200" textAnchor="middle" className="text-xs font-bold">TEACHER</text>
                    <text x="480" y="300" textAnchor="middle" className="text-xs font-bold">STUDENT</text>
                    <text x="410" y="450" textAnchor="middle" className="text-xs font-bold">PEER</text>

                    <text x="300" y="510" textAnchor="middle" className="text-xs font-bold">TEACHER</text>
                    <text x="190" y="450" textAnchor="middle" className="text-xs font-bold">STUDENT</text>
                    <text x="120" y="300" textAnchor="middle" className="text-xs font-bold">PEER</text>

                    {/* Level Labels */}
                    <text x="300" y="80" textAnchor="middle" className="text-xs font-bold">A</text>
                    <text x="300" y="130" textAnchor="middle" className="text-xs font-bold">P</text>
                    <text x="300" y="180" textAnchor="middle" className="text-xs font-bold">B</text>

                    <text x="450" y="300" textAnchor="middle" className="text-xs font-bold">A</text>
                    <text x="400" y="300" textAnchor="middle" className="text-xs font-bold">P</text>
                    <text x="350" y="300" textAnchor="middle" className="text-xs font-bold">B</text>

                    <text x="300" y="520" textAnchor="middle" className="text-xs font-bold">A</text>
                    <text x="300" y="470" textAnchor="middle" className="text-xs font-bold">P</text>
                    <text x="300" y="420" textAnchor="middle" className="text-xs font-bold">B</text>

                    <text x="150" y="300" textAnchor="middle" className="text-xs font-bold">A</text>
                    <text x="200" y="300" textAnchor="middle" className="text-xs font-bold">P</text>
                    <text x="250" y="300" textAnchor="middle" className="text-xs font-bold">B</text>
                </svg>
            </div>

            {/* Key Diagram */}
            <div className="w-1/4 px-4">
                <div className="text-sm font-bold mb-2">KEY</div>
                <svg viewBox="0 0 300 200" className="w-full">
                    {/* Mini version of wheel for key */}
                    <path d="M 150 100 L 150 0 A 100 100 0 0 1 250 50 Z" fill="#f472b6" />
                    <text x="200" y="40" textAnchor="middle" className="text-xs font-bold">AWARENESS</text>

                    <path d="M 150 100 L 150 50 A 50 50 0 0 1 200 75 L 150 100 Z" fill="#f97316" />
                    <text x="170" y="70" textAnchor="middle" className="text-[8px] font-bold">TEACHER</text>

                    <path d="M 150 100 L 200 75 A 50 50 0 0 1 200 125 L 150 100 Z" fill="#4ade80" />
                    <text x="190" y="100" textAnchor="middle" className="text-[8px] font-bold">STUDENT</text>

                    <path d="M 150 100 L 200 125 A 50 50 0 0 1 150 150 L 150 100 Z" fill="#a5b4fc" />
                    <text x="170" y="130" textAnchor="middle" className="text-[8px] font-bold">PEER</text>

                    {/* Circles */}
                    <circle cx="150" cy="100" r="50" fill="none" stroke="#000" strokeWidth="1" />
                    <circle cx="150" cy="100" r="33" fill="none" stroke="#000" strokeWidth="1" />
                    <circle cx="150" cy="100" r="16" fill="white" stroke="#000" strokeWidth="1" />

                    {/* Center Text */}
                    <text x="150" y="100" textAnchor="middle" className="text-[8px] font-bold">LEVEL</text>

                    {/* Dividing Lines */}
                    <line x1="150" y1="0" x2="150" y2="150" stroke="#000" strokeWidth="1" />
                    <line x1="100" y1="100" x2="200" y2="100" stroke="#000" strokeWidth="1" />

                    {/* Level Indicators */}
                    <text x="150" y="30" textAnchor="middle" className="text-[8px] font-bold">A</text>
                    <text x="150" y="47" textAnchor="middle" className="text-[8px] font-bold">P</text>
                    <text x="150" y="64" textAnchor="middle" className="text-[8px] font-bold">B</text>
                </svg>
            </div>
        </div>
    );
};

/**
 * A reusable checkbox component for the strengths and barriers sections.
 * Uses inline styles for PDF compatibility.
 */
const Checkbox = ({ label, checked }) => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '4px',
        borderRadius: '4px',
        backgroundColor: checked ? '#FFF7ED' : 'transparent',
        border: checked ? '1px solid #FDBA74' : '1px solid transparent',
        marginBottom: '2px'
    }}>
        <div style={getCheckboxStyle(checked)}>
            {checked && <span style={{ color: 'white', fontWeight: 'bold', fontSize: '10px' }}>✓</span>}
        </div>
        <span style={{
            fontSize: '11px',
            fontWeight: checked ? '600' : 'normal',
            color: checked ? '#9A3412' : '#374151'
        }}>
            {label}
        </span>
    </div>
);

/**
 * Main TeacherFeedbackPage component.
 */
const TeacherFeedbackPage = ({ assignment }) => {
    // Handle both structured and simple string teacher feedback
    let safeData = {};

    if (assignment?.teacher_feedback) {
        if (typeof assignment.teacher_feedback === 'object') {
            safeData = assignment.teacher_feedback;
        } else {
            // Convert simple string to structured format for display
            safeData = {
                feedback_text: assignment.teacher_feedback,
                observations: assignment.teacher_feedback,
                areas_of_strength: [],
                barriers_to_success: [],
                support_actions: [],
                other_strength: '',
                other_barrier: ''
            };
        }
    }

    // Also include observational notes if available
    if (assignment?.observational_notes && assignment.observational_notes !== assignment?.teacher_feedback) {
        safeData.observations = (safeData.observations || '') + '\n' + assignment.observational_notes;
    }

    // Define the options for strengths and barriers
    const strengthOptions = [
        "Follow Instructions", "Collaboration",
        "Independent Work", "Responsible",
        "Communication", "Creative",
        "Solution-focused Thinking", "Concentration",
        "Empathy",
        "Organization & Prioritization"
    ];

    const barrierOptions = [
        "Lack of Attention", "Peer Pressure",
        "Lack of Motivation", "Undefined Goals",
        "Lack of Preparation", "Domestic Issues",
        "Inappropriate behaviour in classroom",
        "Severe illness or injury",
        "None"
    ];

    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.6',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            breakInside: 'avoid',
            pageBreakInside: 'avoid'
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                style={{
                    borderTop: '80px solid #f1905b',
                    borderLeft: '80px solid transparent'
                }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                style={{
                    borderBottom: '80px solid #F97316',
                    borderRight: '80px solid transparent'
                }}>
            </div>

            <table className="w-full border border-black text-sm bg-white">
                {/* Heading Row */}
                <tbody>
                    <tr>
                        <td colSpan={3} className="border-b border-black text-center bg-[#ffedd5] font-bold text-lg p-2">
                            TEACHER'S FEEDBACK
                        </td>
                    </tr>
                    {/* Student Progress Wheel heading */}
                    <tr>
                        <td colSpan={3} className=" text-center font-bold text-md p-2">
                            STUDENT PROGRESS WHEEL
                        </td>
                    </tr>
                    {/* Image Row */}
                    <tr>
                        <td colSpan={3} className=" p-3 text-center align-middle">
                            <img src={image} alt="Progress Wheel" className="mx-auto" style={{ maxHeight: 320 }} />
                        </td>
                    </tr>
                    {/* How to use the Assessment Wheel */}
                    <tr>
                        <td colSpan={3} className="border-b border-black p-3 align-top">
                            <div className="font-bold">How to use the Assessment Wheel?</div>
                            <div>
                                Shade the segment of the circle that best represents the student's performance on each
                                ability as indicated on the progress grids marked by the student and the peer as well as the
                                teacher's assessment of the student's performance on the activity. The levels of abilities
                                grow in strength outwards from the center of the wheel.
                            </div>
                        </td>
                    </tr>
                    {/* Strength, Barriers, Support Table Section */}
                    <tr>
                        <td className="border-b border-black p-3 align-top w-[40%]">
                            <div className="font-bold mb-2">Areas of Strength (✓ all that apply)</div>
                            <div className="grid grid-cols-2 gap-1">
                                {strengthOptions.map(opt => (
                                    <Checkbox
                                        key={opt}
                                        label={opt}
                                        checked={(safeData.areas_of_strength || []).includes(opt)}
                                    />
                                ))}
                            </div>
                            <div className="flex items-baseline mt-2">
                                <span className="text-xs mr-1">Any other</span>
                                <span style={{
                                    borderBottom: safeData.other_strength ? '2px solid #F97316' : '1px solid #9CA3AF',
                                    flex: 1,
                                    marginLeft: '4px',
                                    padding: '2px 4px',
                                    borderRadius: '2px',
                                    backgroundColor: safeData.other_strength ? '#FFF7ED' : 'transparent',
                                    fontWeight: safeData.other_strength ? '600' : 'normal',
                                    color: safeData.other_strength ? '#9A3412' : 'inherit'
                                }}>
                                    {safeData.other_strength || ''}
                                </span>
                            </div>
                        </td>
                        <td className="border-b border-black border-l border-black p-3 align-top w-[30%]">
                            <div className="font-bold mb-2">Barrier(s) to Success (✓ all that apply)</div>
                            <div className="flex flex-col space-y-1">
                                {barrierOptions.map(opt => (
                                    <Checkbox
                                        key={opt}
                                        label={opt}
                                        checked={(safeData.barriers_to_success || []).includes(opt)}
                                    />
                                ))}
                            </div>
                            <div className="flex items-baseline mt-2">
                                <span className="text-xs mr-1">Any other</span>
                                <span style={{
                                    borderBottom: safeData.other_barrier ? '2px solid #F97316' : '1px solid #9CA3AF',
                                    flex: 1,
                                    marginLeft: '4px',
                                    padding: '2px 4px',
                                    borderRadius: '2px',
                                    backgroundColor: safeData.other_barrier ? '#FFF7ED' : 'transparent',
                                    fontWeight: safeData.other_barrier ? '600' : 'normal',
                                    color: safeData.other_barrier ? '#9A3412' : 'inherit'
                                }}>
                                    {safeData.other_barrier || ''}
                                </span>
                            </div>
                        </td>
                        <td className="border-b border-black border-l border-black p-3 align-top w-[30%]">
                            <div className="font-bold mb-2">How I can help the student progress further?</div>
                            <div className="space-y-4 mt-2">
                                {(safeData.support_actions || ["", "", "", "", "", ""]).map((action, i) => (
                                    <div key={i} style={{
                                        borderBottom: action && action.trim() ? '2px solid #F97316' : '1px solid #9CA3AF',
                                        minHeight: '18px',
                                        padding: '2px 4px',
                                        borderRadius: '2px',
                                        backgroundColor: action && action.trim() ? '#FFF7ED' : 'transparent',
                                        fontWeight: action && action.trim() ? '600' : 'normal',
                                        color: action && action.trim() ? '#9A3412' : 'inherit'
                                    }}>
                                        {action}
                                    </div>
                                ))}
                            </div>
                        </td>
                    </tr>
                    {/* Teacher's Observations and Recommendations */}
                    <tr>
                        <td colSpan={3} className="p-2 align-top">
                            <div className="font-bold border-b border-black pb-1 mb-2">Teacher's Observations and
                                Recommendations
                            </div>
                            <div style={{
                                minHeight: '40px',
                                padding: '8px',
                                borderRadius: '4px',
                                backgroundColor: (safeData.observations || safeData.feedback_text || assignment?.observational_notes) ? '#FFF7ED' : '#F9FAFB',
                                border: (safeData.observations || safeData.feedback_text || assignment?.observational_notes) ? '1px solid #FDBA74' : 'none',
                                fontWeight: (safeData.observations || safeData.feedback_text || assignment?.observational_notes) ? '500' : 'normal',
                                color: (safeData.observations || safeData.feedback_text || assignment?.observational_notes) ? '#9A3412' : 'inherit'
                            }}>
                                {safeData.observations || safeData.feedback_text || assignment?.observational_notes || ''}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div className="border-b-2 mt-20 border-[#fbd3b1] w-full mb-1" />
        </div>
    );
};

export default TeacherFeedbackPage;